import { useState } from 'react';
import { giftCards as giftCardsApi } from '../../utils/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Gift, CheckCircle, XCircle, Search } from 'lucide-react';

export default function GiftCardCheck() {
  const [code, setCode]       = useState('');
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const check = async () => {
    if (!code.trim()) return;
    setLoading(true); setResult(null); setError('');
    try {
      const r = await giftCardsApi.check(code.trim());
      setResult(r.data.giftCard);
    } catch(e) {
      setError(e.response?.data?.error || 'Code invalide');
    } finally { setLoading(false); }
  };

  const remaining = result ? parseFloat(result.remaining_amount) : 0;
  const initial   = result ? parseFloat(result.initial_amount) : 0;
  const pct       = result ? Math.round((remaining / initial) * 100) : 0;

  return (
    <div style={{ minHeight:'100vh', background:'#0a0b0f', display:'flex',
      alignItems:'center', justifyContent:'center', padding:20,
      fontFamily:'Inter, -apple-system, sans-serif' }}>
      <div style={{ width:'100%', maxWidth:440 }}>
        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <div style={{ fontSize:48, marginBottom:12 }}>🎁</div>
          <h1 style={{ color:'#fff', fontSize:24, fontWeight:800, margin:'0 0 8px' }}>
            Vérifier un bon cadeau
          </h1>
          <p style={{ color:'#666', fontSize:14, margin:0 }}>
            Entrez le code de votre bon cadeau pour consulter son solde
          </p>
        </div>

        {/* Champ de saisie */}
        <div style={{ background:'#13141a', borderRadius:16, padding:24,
          border:'1px solid #2a2b35', marginBottom:16 }}>
          <div style={{ display:'flex', gap:10 }}>
            <input
              value={code}
              onChange={e => { setCode(e.target.value.toUpperCase()); setResult(null); setError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); check(); } }}
              placeholder="EG-XXXXXXXX"
              style={{ flex:1, padding:'12px 14px', background:'#1e1f28',
                border:'1px solid #2a2b35', borderRadius:10, color:'#fff',
                fontSize:16, fontFamily:'monospace', fontWeight:600,
                outline:'none', letterSpacing:'0.05em' }}/>
            <button onClick={check} disabled={loading || !code.trim()}
              style={{ padding:'12px 18px', background:'#7c5cfc', border:'none',
                borderRadius:10, color:'#fff', cursor:'pointer', fontSize:14,
                fontWeight:600, display:'flex', alignItems:'center', gap:8,
                opacity: (!code.trim() || loading) ? 0.5 : 1 }}>
              {loading ? '...' : <><Search size={16}/> Vérifier</>}
            </button>
          </div>
        </div>

        {/* Erreur */}
        {error && (
          <div style={{ background:'rgba(255,77,109,0.1)', border:'1px solid rgba(255,77,109,0.3)',
            borderRadius:12, padding:'16px 20px', display:'flex', alignItems:'center', gap:12 }}>
            <XCircle size={20} color="#ff4d6d"/>
            <span style={{ color:'#ff4d6d', fontSize:14, fontWeight:600 }}>{error}</span>
          </div>
        )}

        {/* Résultat */}
        {result && (
          <div style={{ background:'#13141a', border:'1px solid #2a2b35',
            borderRadius:16, padding:24 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
              <CheckCircle size={20} color="#00d2b4"/>
              <span style={{ color:'#00d2b4', fontWeight:700, fontSize:15 }}>Bon cadeau valide</span>
            </div>

            {/* Code */}
            <div style={{ fontFamily:'monospace', fontSize:22, fontWeight:800,
              letterSpacing:'0.1em', color:'#a387fd', textAlign:'center',
              marginBottom:20, padding:'12px', background:'rgba(124,92,252,0.08)',
              borderRadius:10 }}>
              {result.code}
            </div>

            {/* Solde */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:20 }}>
              <div style={{ background:'#1e1f28', borderRadius:10, padding:'14px 16px' }}>
                <div style={{ fontSize:11, color:'#666', marginBottom:4 }}>Valeur initiale</div>
                <div style={{ fontSize:20, fontWeight:800, color:'#fff' }}>{initial.toFixed(2)} €</div>
              </div>
              <div style={{ background:'rgba(0,210,180,0.08)', border:'1px solid rgba(0,210,180,0.2)',
                borderRadius:10, padding:'14px 16px' }}>
                <div style={{ fontSize:11, color:'#666', marginBottom:4 }}>Solde disponible</div>
                <div style={{ fontSize:20, fontWeight:800, color:'#00d2b4' }}>{remaining.toFixed(2)} €</div>
              </div>
            </div>

            {/* Barre de progression */}
            <div style={{ marginBottom:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12,
                color:'#666', marginBottom:6 }}>
                <span>Utilisé : {(initial - remaining).toFixed(2)} €</span>
                <span>{pct}% restant</span>
              </div>
              <div style={{ background:'#2a2b35', borderRadius:20, height:8, overflow:'hidden' }}>
                <div style={{ width:`${pct}%`, height:'100%',
                  background:'linear-gradient(90deg,#7c5cfc,#00d2b4)',
                  borderRadius:20, transition:'width 0.5s' }}/>
              </div>
            </div>

            {/* Expiration */}
            <div style={{ fontSize:13, color:'#555', textAlign:'center' }}>
              Valable jusqu'au {format(new Date(result.expires_at), 'd MMMM yyyy', {locale:fr})}
            </div>

            {/* Message perso */}
            {result.message && (
              <div style={{ marginTop:16, padding:'12px 16px', background:'rgba(124,92,252,0.06)',
                border:'1px solid rgba(124,92,252,0.15)', borderRadius:10,
                fontSize:13, color:'#aaa', fontStyle:'italic', textAlign:'center' }}>
                "{result.message}"
              </div>
            )}
          </div>
        )}

        <div style={{ textAlign:'center', marginTop:24, fontSize:12, color:'#333' }}>
          Propulsé par EscapeOS
        </div>
      </div>
    </div>
  );
}
